import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, CheckCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await resetPassword(token, newPassword);
    
    if (result.success) {
      setSuccess(true);
      toast.success('Password reset successful!');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
        <div className="w-full max-w-md">
          <div className="bg-[#121212] border border-zinc-800 p-8 text-center" data-testid="reset-success">
            <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              Password Reset Complete
            </h1>
            <p className="text-zinc-400 mb-6">
              Your password has been successfully reset. You can now login with your new password.
            </p>
            <Link to="/login">
              <Button className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!token) {
    return (
      <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
        <div className="w-full max-w-md">
          <div className="bg-[#121212] border border-zinc-800 p-8 text-center" data-testid="no-token">
            <KeyRound className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-4">
              Invalid Reset Link
            </h1>
            <p className="text-zinc-400 mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/login">
              <Button variant="outline" className="border-zinc-800 text-white hover:bg-zinc-900 rounded-none">
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
      <div className="w-full max-w-md">
        <div className="bg-[#121212] border border-zinc-800 p-8" data-testid="reset-password-form">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-cyan-400/10 border border-cyan-400/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-2">
              Reset Password
            </h1>
            <p className="text-zinc-500 text-sm">
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                New Password
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 pr-12 focus:border-cyan-400"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                Confirm New Password
              </Label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="reset-submit-button"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}
